import { isDefined } from "@omni/utils/helper.utils";
import { createContext, ReactNode, RefObject, use } from "react";
import { GroupBase, OptionsOrGroups, SingleValue } from "react-select";
import Select from "react-select/base";

import { SearchItem } from "@/components/Store/StoreSearchInput/storeSearchInput.types";

type StoreSearchInputContextValue = {
    disabled: boolean;
    focussed: boolean;
    open: boolean;
    loading: boolean;
    clearable: boolean;
    asyncRef: RefObject<Select<
        SearchItem,
        false,
        GroupBase<SearchItem>
    > | null>;
    focusRef: RefObject<SearchItem | null>;
    menuPortalTarget: HTMLElement | null;
    value: SearchItem | undefined;
    placeholder: string;
    loadingText: string;
    "aria-label": string;
    loadOptions: (
        inputValue: string,
        callback: (
            options: OptionsOrGroups<SearchItem, GroupBase<SearchItem>>
        ) => void
    ) => void;
    onSearchClick: () => void;
    onUseLocation: (latitude: number, longitude: number) => void;
    onLocationError: (errorMessage: string) => void;
    onChange: (newValue: SingleValue<SearchItem>) => void;
    onFocus: () => void;
    onBlur: () => void;
    onInputChange: (newValue: string) => void;
};

type StoreSearchInputContextProviderProps = {
    disabled: boolean;
    focussed: boolean;
    open: boolean;
    loading: boolean;
    clearable: boolean;
    asyncRef: RefObject<Select<
        SearchItem,
        false,
        GroupBase<SearchItem>
    > | null>;
    focusRef: RefObject<SearchItem | null>;
    menuPortalTarget: HTMLElement | null;
    value: SearchItem | undefined;
    placeholder: string;
    loadingText: string;
    "aria-label": string;
    loadOptions: (
        inputValue: string,
        callback: (
            options: OptionsOrGroups<SearchItem, GroupBase<SearchItem>>
        ) => void
    ) => void;
    onSearchClick: () => void;
    onUseLocation: (latitude: number, longitude: number) => void;
    onLocationError: (errorMessage: string) => void;
    onChange: (newValue: SingleValue<SearchItem>) => void;
    onFocus: () => void;
    onBlur: () => void;
    onInputChange: (newValue: string) => void;
    children: ReactNode;
};

export const StoreSearchInputContext = createContext<
    StoreSearchInputContextValue | undefined
>(undefined);
StoreSearchInputContext.displayName = "StoreSearchInputContext";

export const StoreSearchInputContextProvider = ({
    disabled,
    focussed,
    open,
    loading,
    clearable,
    asyncRef,
    focusRef,
    menuPortalTarget,
    value,
    placeholder,
    loadingText,
    "aria-label": ariaLabel,
    loadOptions,
    onSearchClick,
    onUseLocation,
    onLocationError,
    onChange,
    onFocus,
    onBlur,
    onInputChange,
    children
}: StoreSearchInputContextProviderProps) => {
    return (
        <StoreSearchInputContext
            value={{
                disabled: disabled,
                focussed: focussed,
                open: open,
                loading: loading,
                clearable: clearable,
                asyncRef: asyncRef,
                focusRef: focusRef,
                menuPortalTarget: menuPortalTarget,
                value: value,
                placeholder: placeholder,
                loadingText: loadingText,
                "aria-label": ariaLabel,
                onChange: onChange,
                onFocus: onFocus,
                onBlur: onBlur,
                onInputChange: onInputChange,
                loadOptions: loadOptions,
                onSearchClick: onSearchClick,
                onUseLocation: onUseLocation,
                onLocationError: onLocationError
            }}
        >
            {children}
        </StoreSearchInputContext>
    );
};

export const useStoreSearchInputContext = () => {
    const context = use<StoreSearchInputContextValue | undefined>(
        StoreSearchInputContext
    );

    if (!isDefined(context)) {
        throw new Error(`${StoreSearchInputContext.displayName} is undefined.`);
    }

    return context;
};
